import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [accountType, setAccountType] = useState(() => {
    // Remember user's last choice or default to 'main'
    return localStorage.getItem('preferredAccountType') || 'main';
  });
  const [error, setError] = useState('');
  const [isVerificationError, setIsVerificationError] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleAccountTypeChange = (type) => {
    setAccountType(type);
    localStorage.setItem('preferredAccountType', type);
    // Clear any existing errors when switching account types
    setError('');
    setIsVerificationError(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsVerificationError(false);
    setLoading(true);

    try {
      await login({ email, password, accountType });
      navigate('/dashboard');
    } catch (err) {
      if (err.response?.data?.message === 'Please verify your email before logging in') {
        setError('Your email address is not verified. Please check your inbox for the verification email.');
        setIsVerificationError(true);
      } else if (err.response?.data?.message?.includes('No main account found')) {
        setError('No main account found with this email address. Try selecting "Sub Account" or check your email.');
      } else if (err.response?.data?.message?.includes('No sub account found')) {
        setError('No sub account found with this email address. Try selecting "Main Account" or check your email.');
      } else {
        setError('Failed to sign in. Please check your credentials and account type.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    // You can implement the forgot password functionality here
    // For now, we'll just alert the user
    alert('Forgot password functionality will be implemented in the future.');
  };

  const handleResendVerification = () => {
    navigate('/resend-verification', { state: { email } });
  };

  return (
    <div className="flex flex-col justify-center" style={{ minHeight: '100vh', padding: '3rem 1.5rem' }}>
      <div style={{ maxWidth: '28rem', margin: '0 auto', width: '100%' }}>
        <h2 className="text-3xl font-bold text-center mb-6">
          Sign in to your account
        </h2>
      </div>

      <div style={{ maxWidth: '28rem', margin: '2rem auto 0', width: '100%' }}>
        <div className="card">
          <form className="form" onSubmit={handleSubmit}>
            {error && (
              <div style={{ backgroundColor: '#FEE2E2', padding: '1rem', borderRadius: '0.375rem', marginBottom: '1.5rem' }}>
                <p style={{ color: '#B91C1C', fontSize: '0.875rem', fontWeight: '500' }}>{error}</p>
                {isVerificationError && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      style={{ 
                        color: '#B91C1C', 
                        fontWeight: '500',
                        fontSize: '0.875rem',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        textDecoration: 'underline'
                      }}
                    >
                      Resend verification email
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Account Type</label>
              <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="accountType"
                    value="main"
                    checked={accountType === 'main'}
                    onChange={(e) => handleAccountTypeChange(e.target.value)}
                    style={{ 
                      marginRight: '0.5rem',
                      accentColor: 'var(--color-primary)'
                    }}
                  />
                  <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>Main Account</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="accountType"
                    value="sub"
                    checked={accountType === 'sub'}
                    onChange={(e) => handleAccountTypeChange(e.target.value)}
                    style={{ 
                      marginRight: '0.5rem',
                      accentColor: 'var(--color-primary)'
                    }}
                  />
                  <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>Sub Account</span>
                </label>
              </div>
              <p style={{ fontSize: '0.75rem', color: 'var(--color-gray-500)', marginTop: '0.25rem' }}>
                {accountType === 'main' 
                  ? 'Sign in as a main account owner with full access to projects and team management.'
                  : 'Sign in as a sub account with access to specific projects assigned by your team administrator.'
                }
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input"
                />
              </div>
            </div>

            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  style={{ height: '1rem', width: '1rem', borderRadius: '0.25rem', borderColor: 'var(--color-gray-300)' }}
                />
                <label htmlFor="remember-me" className="ml-2 text-sm">
                  Remember me
                </label>
              </div>

              <div>
                <Link
                  to="/forgot-password"
                  style={{ 
                    fontSize: '0.875rem', 
                    fontWeight: '500',
                    color: 'var(--color-primary)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textDecoration: 'none'
                  }}
                >
                  Forgot your password?
                </Link>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className={`btn text-smd btn-primary ${loading ? 'btn-disabled' : ''}`}
                style={{ width: '100%' }}
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>

          <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
            <Link 
              to="/resend-verification" 
              style={{ 
                fontSize: '0.875rem',
                color: 'var(--color-primary)',
                textDecoration: 'none'
              }}
            >
              Didn't receive verification email?
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 